import { authHeaders, fetchWithAuthRetry, fetchWithTimeout, getApiUrl, parseApiError } from './common';
import type { PagedData, PaginationMeta } from './common';

/**
 * Skill 패키지 API 클라이언트.
 *
 * 목록 응답에는 파일 본문이 없다(메타 + sha256만). 본문이 필요하면 `fetchSkillPackage`를 쓴다 —
 * 목록 화면이 실수로 전체 패키지를 끌어오는 일을 타입 수준에서 막기 위한 구분이다.
 */

export type SkillMember = {
  id: string;
  nickname: string;
  email: string;
};

export type SkillFileMeta = {
  relativePath: string;
  kind: string;
  mimeType: string | null;
  sizeBytes: number;
  sha256: string;
};

export type SkillTextFile = SkillFileMeta & {
  content: string;
};

/** 자산. 현재 패키지는 presigned URL을 함께 주고, 이력 스냅샷은 메타만 준다. */
export type SkillAssetFile = SkillFileMeta & {
  kind: 'BINARY';
  downloadUrl?: string;
};

export type SkillFile = SkillTextFile | SkillAssetFile;

export const isSkillAssetFile = (file: SkillFile): file is SkillAssetFile => file.kind === 'BINARY';

export type Skill = {
  id: string;
  projectId: string;
  repositoryId?: string | null;
  slug: string;
  title: string;
  description: string;
  memberId: string;
  member?: SkillMember | null;
  scope: string;
  version: string;
  contentTokenCount?: number | null;
  legacyConventionId?: string | null;
  files: SkillFileMeta[];
  isSharedByMe?: boolean;
  archivedAt?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type SkillPackage = Omit<Skill, 'files'> & {
  files: SkillFile[];
};

export type SkillHistoryItem = {
  id: string;
  skillId: string;
  title: string;
  description: string;
  version: string;
  memberId: string;
  member?: SkillMember | null;
  createdAt: string;
};

export type SkillHistorySnapshot = SkillHistoryItem & { files: SkillFile[] };

export type SkillUsageSummaryRow = {
  skillId: string;
  title: string;
  slug: string;
  totalRef: number;
  completedRef: number;
  partialRef: number;
  failedRef: number;
  avgQualityScore: number | null;
  successRate: number | null;
  planRef: number;
  planOnlyRef: number;
  reportOnlyRef: number;
  bothRef: number;
};

export type SkillUsageSummary = {
  data: SkillUsageSummaryRow[];
  period: { from: string; to: string };
};

type SkillListResponse = { data: Skill[]; meta: PaginationMeta };
type SkillHistoryEntryResponse = { data: SkillHistorySnapshot };
type SkillResponse = { data: Skill };
type SkillPackageResponse = { data: SkillPackage };
type SkillHistoryResponse = { data: SkillHistoryItem[]; meta: PaginationMeta };

const skillsUrl = (projectId: string, path = '') => `${getApiUrl()}/api/projects/${projectId}/skills${path}`;

export const fetchSkillUsageSummary = async (
  token: string,
  projectId: string,
  options: { from?: string; to?: string } = {},
): Promise<SkillUsageSummary> => {
  const queryParams = new URLSearchParams();
  if (options.from) queryParams.set('from', options.from);
  if (options.to) queryParams.set('to', options.to);
  const response = await fetchWithAuthRetry(
    skillsUrl(projectId, `/usage-summary${queryParams.toString() ? `?${queryParams.toString()}` : ''}`),
    { cache: 'no-store', headers: authHeaders(token) },
  );
  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch skill usage summary: ${response.status}`);
  }
  return (await response.json()) as SkillUsageSummary;
};

export const fetchSkillsPaged = async (
  token: string,
  projectId: string,
  params?: SkillListParams,
): Promise<PagedData<Skill>> => {
  const queryParams = new URLSearchParams();
  if (params?.sharedByMe) queryParams.set('sharedByMe', 'true');
  if (params?.scope) queryParams.set('scope', params.scope);
  if (params?.search) queryParams.set('search', params.search);
  if (params?.legacyConventionId) queryParams.set('legacyConventionId', params.legacyConventionId);
  if (params?.page) queryParams.set('page', String(params.page));
  if (params?.pageSize) queryParams.set('pageSize', String(params.pageSize));

  const response = await fetchWithAuthRetry(
    skillsUrl(projectId, queryParams.toString() ? `?${queryParams.toString()}` : ''),
    { cache: 'no-store', headers: authHeaders(token) },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch skills: ${response.status}`);
  }

  const payload = (await response.json()) as SkillListResponse;
  return { data: payload.data, meta: payload.meta };
};

export const fetchSkillById = async (token: string, projectId: string, skillId: string): Promise<Skill> => {
  const response = await fetchWithAuthRetry(skillsUrl(projectId, `/${skillId}`), {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch skill: ${response.status}`);
  }

  return ((await response.json()) as SkillResponse).data;
};

/**
 * 파일 본문까지 포함한 패키지. 상세 화면에서 파일을 열 때만 호출한다.
 * 자산 인지 플래그로 요청한다 — 없으면 서버가 BINARY를 응답에서 빼 구버전처럼 보인다.
 */
export const fetchSkillPackage = async (token: string, projectId: string, skillId: string): Promise<SkillPackage> => {
  const response = await fetchWithAuthRetry(skillsUrl(projectId, `/${skillId}/download?includeAssets=true`), {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch skill package: ${response.status}`);
  }

  return ((await response.json()) as SkillPackageResponse).data;
};

/** 자산 파일 1건의 내려받기 URL. 미리보기는 HEAD가 아니라 이 GET과 <img loading=lazy>로 한다. */
export const fetchSkillFileDownloadUrl = async (
  token: string,
  projectId: string,
  skillId: string,
  relativePath: string,
  historyId?: string | null,
): Promise<{ relativePath: string; downloadUrl: string }> => {
  const queryParams = new URLSearchParams({ relativePath });
  if (historyId) queryParams.set('historyId', historyId);
  const response = await fetchWithAuthRetry(skillsUrl(projectId, `/${skillId}/files/download-url?${queryParams}`), {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch skill file download URL: ${response.status}`);
  }

  return ((await response.json()) as { data: { relativePath: string; downloadUrl: string } }).data;
};

export const fetchSkillHistory = async (
  token: string,
  projectId: string,
  skillId: string,
): Promise<SkillHistoryItem[]> => {
  const response = await fetchWithAuthRetry(skillsUrl(projectId, `/${skillId}/history`), {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch skill history: ${response.status}`);
  }

  return ((await response.json()) as SkillHistoryResponse).data;
};

/**
 * 이력 한 건 + 그 시점 패키지 스냅샷. 목록(`fetchSkillHistory`)은 메타만 주므로, 과거 버전 본문이
 * 필요할 때만 이 호출을 쓴다 — 목록에 본문을 실으면 이력 수만큼 패키지가 실린다.
 */
export const fetchSkillHistoryEntry = async (
  token: string,
  projectId: string,
  skillId: string,
  historyId: string,
): Promise<SkillHistorySnapshot> => {
  const response = await fetchWithAuthRetry(skillsUrl(projectId, `/${skillId}/history/${historyId}`), {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch skill history entry: ${response.status}`);
  }

  return ((await response.json()) as SkillHistoryEntryResponse).data;
};

export const createSkill = async (
  token: string,
  projectId: string,
  input: { slug: string; files: { relativePath: string; content: string }[]; scope?: string },
): Promise<Skill> => {
  const response = await fetchWithAuthRetry(skillsUrl(projectId), {
    method: 'POST',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to create skill: ${response.status}`);
  }

  return ((await response.json()) as SkillResponse).data;
};

export const updateSkill = async (
  token: string,
  projectId: string,
  skillId: string,
  input: { files: { relativePath: string; content: string }[]; updatedAt: string; scope?: string },
): Promise<Skill> => {
  const response = await fetchWithAuthRetry(skillsUrl(projectId, `/${skillId}`), {
    method: 'PATCH',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to update skill: ${response.status}`);
  }

  return ((await response.json()) as SkillResponse).data;
};

export const deleteSkill = async (token: string, projectId: string, skillId: string): Promise<void> => {
  const response = await fetchWithAuthRetry(skillsUrl(projectId, `/${skillId}`), {
    method: 'DELETE',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to delete skill: ${response.status}`);
  }
};

export type SkillListParams = {
  scope?: string;
  search?: string;
  legacyConventionId?: string;
  sharedByMe?: boolean;
  page?: number;
  pageSize?: number;
};

export type SkillShareScope = 'PUBLIC' | 'TEAM' | 'LINK';
export type SkillShareOptions = {
  includeBody?: boolean;
  includeExecutable?: boolean;
  allowInstall?: boolean;
  expiresAt?: string | null;
};
export type CreateSkillShareInput = SkillShareOptions & {
  scope: SkillShareScope;
};
export type SkillShare = {
  id: string;
  skillId: string;
  ownerMemberId: string;
  createdByMemberId: string;
  scope: SkillShareScope;
  targetTeamId: string | null;
  includeBody: boolean;
  includeExecutable: boolean;
  allowInstall: boolean;
  status: 'ACTIVE' | 'REVOKED';
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
  revokedAt: string | null;
  url: string | null;
  token?: string | null;
};
export type SkillInstall = {
  id: string;
  shareId: string | null;
  sourceSkillId: string | null;
  installedSkillId: string | null;
  sourceVersion: string;
  currentVersion: string;
  isLatest: boolean;
  createdAt: string;
  installedProject: { id: string; name: string };
  installedByMember: { id: string; nickname: string };
};

const fetchSkillSharePage = async <T>(
  token: string,
  projectId: string,
  skillId: string,
  resource: 'shares' | 'installs',
  page: number,
): Promise<PagedData<T>> => {
  const response = await fetchWithAuthRetry(skillsUrl(projectId, `/${skillId}/${resource}?page=${page}&pageSize=100`), {
    cache: 'no-store',
    headers: authHeaders(token),
  });
  if (!response.ok) throw await parseApiError(response, `Failed to fetch skill ${resource}: ${response.status}`);
  return (await response.json()) as PagedData<T>;
};

// 회수 이력이 첫 페이지를 채워도 오래된 활성 공유가 관리 화면에서 누락되지 않도록 전부 읽는다.
export const fetchSkillShares = async (token: string, projectId: string, skillId: string): Promise<SkillShare[]> => {
  const shares: SkillShare[] = [];
  let page = 1;
  while (true) {
    const result = await fetchSkillSharePage<SkillShare>(token, projectId, skillId, 'shares', page);
    shares.push(...result.data);
    if (page >= result.meta.totalPages) return shares;
    page += 1;
  }
};
export const fetchSkillInstalls = (token: string, projectId: string, skillId: string, page = 1) =>
  fetchSkillSharePage<SkillInstall>(token, projectId, skillId, 'installs', page);

export const createSkillShare = async (
  token: string,
  projectId: string,
  skillId: string,
  input: CreateSkillShareInput,
): Promise<SkillShare> => {
  const response = await fetchWithAuthRetry(skillsUrl(projectId, `/${skillId}/shares`), {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });
  if (!response.ok) throw await parseApiError(response, `Failed to create skill share: ${response.status}`);
  return ((await response.json()) as { data: SkillShare }).data;
};
export const updateSkillShare = async (
  token: string,
  projectId: string,
  skillId: string,
  shareId: string,
  input: SkillShareOptions,
): Promise<SkillShare> => {
  const response = await fetchWithAuthRetry(skillsUrl(projectId, `/${skillId}/shares/${shareId}`), {
    method: 'PATCH',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });
  if (!response.ok) throw await parseApiError(response, `Failed to update skill share: ${response.status}`);
  return ((await response.json()) as { data: SkillShare }).data;
};
/** 비인증 공개 링크로 조회한 스킬. 토큰 원문·서버 내부는 싣지 않는다. */
export type PublicSharedSkillFile = {
  id: string;
  relativePath: string;
  kind: string;
  sizeBytes: number;
  sha256: string;
  mimeType: string | null;
  content: string | null;
  downloadUrl: string | null;
};

export type PublicSharedSkill = {
  slug: string;
  title: string;
  description: string;
  version: string;
  includeBody: boolean;
  includeExecutable: boolean;
  allowInstall: boolean;
  expiresAt: string | null;
  files: PublicSharedSkillFile[];
};

type PublicSharedSkillResponse = { data: PublicSharedSkill };

/** 로그인 없이 링크 토큰으로 스킬을 조회한다. 인증 헤더를 싣지 않는다. */
export const fetchPublicSharedSkill = async (shareToken: string): Promise<PublicSharedSkill> => {
  const response = await fetchWithTimeout(`${getApiUrl()}/api/share/skills/${encodeURIComponent(shareToken)}`, {
    cache: 'no-store',
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch shared skill: ${response.status}`, {
      skipRedirectOn401: true,
    });
  }

  const data = (await response.json()) as PublicSharedSkillResponse;
  return data.data;
};

/**
 * 나에게 보이는 공유 목록 1행. 발행자·설치 수는 소비자 계약에 없어 싣지 않는다 —
 * 목록 카드에는 제목·설명·범위·버전만 쓴다.
 */
export type SharedSkillSummary = {
  id: string;
  slug: string;
  title: string;
  description: string;
  version: string;
};

export type SharedSkillListItem = {
  id: string;
  skillId: string;
  scope: SkillShareScope;
  targetTeamId: string | null;
  includeBody: boolean;
  includeExecutable: boolean;
  allowInstall: boolean;
  status: 'ACTIVE' | 'REVOKED';
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
  skill: SharedSkillSummary;
};

/** 소비자 상세 파일 1건. TEXT 본문은 includeBody일 때만 content에 실린다. */
export type SharedSkillFile = {
  relativePath: string;
  kind: string;
  sizeBytes: number;
  sha256: string;
  mimeType: string | null;
  content: string | null;
};

export type SharedSkillDetail = SharedSkillListItem & {
  files: SharedSkillFile[];
};

export type SharedSkillListParams = {
  search?: string;
  page?: number;
  pageSize?: number;
};

const sharedSkillsUrl = (projectId: string, path = '') =>
  `${getApiUrl()}/api/projects/${projectId}/skills/shared${path}`;

/** 소비 프로젝트에서 보이는 공유(PUBLIC + 그 프로젝트 소속 팀의 TEAM)를 한 목록에 조회한다. */
export const fetchSharedSkills = async (
  token: string,
  projectId: string,
  params: SharedSkillListParams = {},
): Promise<PagedData<SharedSkillListItem>> => {
  const queryParams = new URLSearchParams();
  if (params.search) queryParams.set('search', params.search);
  if (params.page) queryParams.set('page', String(params.page));
  if (params.pageSize) queryParams.set('pageSize', String(params.pageSize));

  const response = await fetchWithAuthRetry(
    sharedSkillsUrl(projectId, queryParams.toString() ? `?${queryParams.toString()}` : ''),
    { cache: 'no-store', headers: authHeaders(token) },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch shared skills: ${response.status}`);
  }

  return (await response.json()) as PagedData<SharedSkillListItem>;
};

/** 공유 상세. 노출 단계에 따라 파일 목록·본문을 싣는다. 소비 프로젝트 문맥으로 조회한다. */
export const fetchSharedSkillDetail = async (
  token: string,
  projectId: string,
  shareId: string,
): Promise<SharedSkillDetail> => {
  const response = await fetchWithAuthRetry(sharedSkillsUrl(projectId, `/${encodeURIComponent(shareId)}`), {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch shared skill detail: ${response.status}`);
  }

  return ((await response.json()) as { data: SharedSkillDetail }).data;
};

/**
 * 공유본을 대상 프로젝트에 복제 설치한다. 참조형 구독이 아니라 독립 복사본이 생긴다.
 * 설치 권한(`skills.write`)은 서버가 판정한다.
 */
export const installSharedSkill = async (token: string, projectId: string, shareId: string): Promise<Skill> => {
  const response = await fetchWithAuthRetry(skillsUrl(projectId, '/install'), {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify({ shareId }),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to install shared skill: ${response.status}`);
  }

  return ((await response.json()) as SkillResponse).data;
};

export const revokeSkillShare = async (
  token: string,
  projectId: string,
  skillId: string,
  shareId: string,
): Promise<void> => {
  const response = await fetchWithAuthRetry(skillsUrl(projectId, `/${skillId}/shares/${shareId}`), {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  if (!response.ok) throw await parseApiError(response, `Failed to revoke skill share: ${response.status}`);
};
